import '.n{width:100%;}.ssr-plugin'
import '.b0{max-width:100%;}.ssr-plugin'
import { useQuery } from '@based/sdk/react'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import { Button } from '@app/components/core/button/Button'
import { SafeHtml } from '@app/components/core/safeHtml/SafeHtml'
import { styled } from 'inlines'
import { useMemo } from 'react'
import {
  faDownload,
  faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons'
import type {
  RawViewPlotRegistryData,
  ViewPlotRegistryModel,
} from 'functions/app/plot-registry/view'
import { LoaderWrapper } from '@app/components/blocks/loader/LoaderWrapper'
import { useTranslations } from '@i18n/useLanguage'
import { PlotRegistryStepTemplate } from './PlotRegistryStepTemplate'
import { Image } from '@app/components/core/image/Image'
import useMediaQuery from '@app/util/useMediaQuery'
import { getTitleLevelStyle } from '@app/components/core/typography/styles'

const hasFileExtension = (url: string) =>
  /\.\w{2,5}(\?.*)?$/.test(new URL(url, 'https://placeholder.com').pathname)

type RegionInfoStepProps = {
  plotRegistryId: number
}

export const RegionInfoStep = ({ plotRegistryId }: RegionInfoStepProps) => {
  const t = useTranslations().app.plotRegistry.regionInfo
  const isMobile = useMediaQuery()
  const { data: rawData } = useQuery('view-plot-registry', {
    id: plotRegistryId,
  })

  const plotRegistry = useMemo<ViewPlotRegistryModel | null>(() => {
    if (!rawData) {
      return null
    }
    const raw = rawData as RawViewPlotRegistryData

    return {
      id: raw.id,
      title: raw.title,
      description: raw.description,
      closingRemark: raw.closingRemark,
      phase: raw.phase,
      municipality: raw.municipality,
      phaseSteps: raw.phaseSteps ?? [],
      requiredDocuments: raw.requiredDocuments ?? [],
      applicationWindow: raw.applicationWindows?.[0] ?? undefined,
    }
  }, [rawData])

  if (!plotRegistry) {
    return <LoaderWrapper loading={true} />
  }

  return (
    <PlotRegistryStepTemplate
      leftGap="1.5rem"
      rightGap="1rem"
      leftStyle={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
      rightStyle={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
      left={
        <>
          {plotRegistry.municipality?.image?.src && (
            <Image
              src={plotRegistry.municipality.image.src}
              alt={plotRegistry.municipality.name}
              resizeWidth={160}
              style={{
                width: '10rem',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          )}

          <Title.Page styledAsLevel={2}>
            {plotRegistry.municipality?.name}
          </Title.Page>

          {plotRegistry.description && (
            <div className="n b0">
              <SafeHtml html={plotRegistry.description} />
            </div>
          )}
        </>
      }
      right={
        <>
          <Title.H2 style={getTitleLevelStyle(3, isMobile)}>
            {t.stepsTitle}
          </Title.H2>

          {plotRegistry.phaseSteps.map((phaseStep, index) => (
            <Row
              key={phaseStep.id}
              gap="0.75rem"
              style={{ alignItems: 'flex-start' }}
            >
              <Column
                gap="0.5rem"
                style={{ flex: 1, minWidth: 0, maxWidth: '100%' }}
              >
                <Text.Medium isBold>
                  {index + 1}. {phaseStep.title}
                </Text.Medium>
                {phaseStep.description && (
                  <styled.div className="n b0"
                    style={{
                      '& p': {
                        margin: phaseStep.exampleDocumentUrl
                          ? '0!important'
                          : undefined,
                      },
                    }}
                  >
                    <SafeHtml html={phaseStep.description} />
                  </styled.div>
                )}
                {phaseStep.exampleDocumentUrl && (
                  <Button
                    label={
                      phaseStep.exampleDocumentCtaLabel ?? t.downloadDocument
                    }
                    icon={
                      hasFileExtension(phaseStep.exampleDocumentUrl)
                        ? faDownload
                        : faArrowUpRightFromSquare
                    }
                    iconPosition="left"
                    variant="secondary"
                    style={{ marginBottom: '0.5rem' }}
                    size="small"
                    onClick={() => {
                      const url = phaseStep.exampleDocumentUrl
                      if (url && /^https?:\/\//i.test(url)) {
                        window.open(url, '_blank', 'noopener,noreferrer')
                      }
                    }}
                  />
                )}
              </Column>
            </Row>
          ))}
        </>
      }
    />
  )
}
